import { useLang, track } from "@/context/LanguageContext";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/Editorial";
import { usePageMeta } from "@/lib/seo";
import { IMAGES } from "@/data/config";

// ── Bsport gift card checkout links ──
const BSPORT_GIFT_35 = "https://backoffice.bsport.io/checkout/6040/giftcard/21787/?force=true";
const BSPORT_GIFT_70 = "https://backoffice.bsport.io/checkout/6040/giftcard/21788/?force=true";
const BSPORT_GIFT_100 = "https://backoffice.bsport.io/checkout/6040/giftcard/21789/?force=true";
const BSPORT_GIFT_165 = "https://backoffice.bsport.io/checkout/6040/giftcard/21790/?force=true";
const BSPORT_GIFT_250 = "https://backoffice.bsport.io/checkout/6040/giftcard/21791/?force=true";
const BSPORT_PRIVATE_99 = "https://backoffice.bsport.io/checkout/6040/giftcard/21792/?force=true";

const COPY = {
  en: {
    label: "Gift Cards",
    heroTitle: "Give the gift of Maison Core",
    heroLead: "Pilates, movement and time for yourself. Choose a Maison Core gift and let them decide how they want to move.",
    heroCta: "Explore Gift Cards",
    favourite: "Maison Favourite",
    cards: [
      { name: "One Class Gift", price: "€35", body: "Give someone one Maison Core Reformer class. A 45-minute session focused on strength, control and mindful movement.", cta: "Gift One Class", url: BSPORT_GIFT_35 },
      { name: "Two Class Gift", price: "€70", body: "Two Maison Core Reformer classes to enjoy at their own pace. A thoughtful introduction to movement, strength and control.", cta: "Gift Two Classes", url: BSPORT_GIFT_70 },
      { name: "Maison Core Gift Card", price: "€100", body: "A €100 Maison Core gift card to spend on classes, class packs or selected Maison Core experiences. The perfect gift with the freedom to choose.", cta: "Gift €100", url: BSPORT_GIFT_100, featured: true },
      { name: "Five Class Gift", price: "€165", body: "Give the gift of five Maison Core Reformer classes. Perfect for building a rhythm and experiencing the Maison Core method over multiple sessions.", cta: "Gift Five Classes", url: BSPORT_GIFT_165 },
      { name: "Maison Core Premium Gift", price: "€250", body: "A €250 gift card for the full Maison Core experience. The recipient can choose how to use their credit across classes, packs or selected private sessions.", cta: "Gift €250", url: BSPORT_GIFT_250 },
    ],
    personalLabel: "A more personal gift",
    personal: { name: "Private Pilates 1:1 Gift", price: "€99", body: "A private 45-minute Pilates Reformer session tailored entirely to the individual. Personal guidance, focused attention and a training experience designed around their goals.", cta: "Gift Private Pilates", url: BSPORT_PRIVATE_99 },
    howLabel: "How it works",
    steps: [
      { t: "Choose your gift", b: "Select the Maison Core experience or amount you would like to give." },
      { t: "Purchase online", b: "Complete the purchase securely through our booking partner." },
      { t: "Let them choose their moment", b: "The recipient can redeem their gift and book their Maison Core experience." },
    ],
    closingTitle: "A gift designed to make them feel good.",
    closingBody: "From a first Reformer class to a private Pilates experience, give them something they can truly enjoy.",
    closingCta: "Choose a Gift",
  },
  nl: {
    label: "Cadeaubonnen",
    heroTitle: "Geef Maison Core cadeau",
    heroLead: "Pilates, beweging en tijd voor jezelf. Kies een Maison Core cadeau en laat de ander bepalen hoe die wil bewegen.",
    heroCta: "Bekijk cadeaubonnen",
    favourite: "Maison Favourite",
    cards: [
      { name: "One Class Gift", price: "€35", body: "Geef iemand één Maison Core Reformer-les. Een sessie van 45 minuten gericht op kracht, controle en bewust bewegen.", cta: "Geef één les", url: BSPORT_GIFT_35 },
      { name: "Two Class Gift", price: "€70", body: "Twee Maison Core Reformer-lessen om in eigen tempo van te genieten. Een attente kennismaking met beweging, kracht en controle.", cta: "Geef twee lessen", url: BSPORT_GIFT_70 },
      { name: "Maison Core Gift Card", price: "€100", body: "Een cadeaubon van €100 te besteden aan lessen, lessenkaarten of geselecteerde Maison Core ervaringen. Het perfecte cadeau met vrijheid van keuze.", cta: "Geef €100", url: BSPORT_GIFT_100, featured: true },
      { name: "Five Class Gift", price: "€165", body: "Geef vijf Maison Core Reformer-lessen cadeau. Perfect om een ritme op te bouwen en de Maison Core methode over meerdere sessies te ervaren.", cta: "Geef vijf lessen", url: BSPORT_GIFT_165 },
      { name: "Maison Core Premium Gift", price: "€250", body: "Een cadeaubon van €250 voor de volledige Maison Core ervaring. De ontvanger kiest zelf hoe het tegoed te besteden aan lessen, kaarten of geselecteerde privésessies.", cta: "Geef €250", url: BSPORT_GIFT_250 },
    ],
    personalLabel: "Een persoonlijker cadeau",
    personal: { name: "Private Pilates 1:1 Gift", price: "€99", body: "Een privé Pilates Reformer-sessie van 45 minuten, volledig afgestemd op de persoon. Persoonlijke begeleiding, gerichte aandacht en een training rond hun doelen.", cta: "Geef Private Pilates", url: BSPORT_PRIVATE_99 },
    howLabel: "Hoe het werkt",
    steps: [
      { t: "Kies je cadeau", b: "Selecteer de Maison Core ervaring of het bedrag dat je wilt geven." },
      { t: "Koop online", b: "Rond de aankoop veilig af via onze boekingspartner." },
      { t: "Laat hen het moment kiezen", b: "De ontvanger verzilvert het cadeau en boekt zijn of haar Maison Core ervaring." },
    ],
    closingTitle: "Een cadeau dat goed voelt.",
    closingBody: "Van een eerste Reformer-les tot een privé Pilates-ervaring — geef iets waar ze écht van genieten.",
    closingCta: "Kies een cadeau",
  },
};

const scrollToCards = () => document.getElementById("gift-cards-grid")?.scrollIntoView({ behavior: "smooth" });

function GiftCard({ card, c, i }) {
  const featured = card.featured;
  return (
    <Reveal delay={i * 0.06}>
      <div
        className={`flex h-full flex-col justify-between gap-10 p-8 transition-colors duration-500 md:p-10 ${
          featured ? "bg-onyx text-petal" : "border border-onyx/15 bg-transparent text-onyx"
        }`}
        data-testid={`gift-card-${i}`}
      >
        <div>
          {featured && <p className="label mb-5 text-blush">{c.favourite}</p>}
          <div className={`flex items-baseline justify-between gap-4 border-b pb-5 ${featured ? "border-petal/15" : "border-onyx/10"}`}>
            <h3 className="font-serif text-3xl leading-tight">{card.name}</h3>
            <span className={`font-serif text-3xl ${featured ? "text-blush" : "text-terracotta"}`}>{card.price}</span>
          </div>
          <p className={`mt-5 font-sans text-base leading-relaxed ${featured ? "text-petal/75" : "text-onyx/65"}`}>{card.body}</p>
        </div>
        <a
          href={card.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("gift_card_click", { gift: card.name })}
          className={`label py-4 text-center transition-colors duration-500 ${
            featured ? "border border-petal bg-petal text-onyx hover:bg-transparent hover:text-petal" : "border border-onyx hover:bg-onyx hover:text-petal"
          }`}
          data-testid={`gift-card-cta-${i}`}
        >
          {card.cta}
        </a>
      </div>
    </Reveal>
  );
}

export default function GiftCards() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;

  // Public page — dynamic SEO like other pages
  usePageMeta(
    lang === "nl"
      ? {
          title: "Cadeaubonnen — Maison Core Reformer Pilates, Amsterdam",
          description: "Geef Maison Core cadeau — van één Reformer-les (€35) tot een privé Pilates-ervaring. Cadeaubonnen van €35 tot €250, direct online te bestellen.",
          canonical: "https://maisoncore.nl/gift-cards",
        }
      : {
          title: "Gift Cards — Maison Core Reformer Pilates, Amsterdam",
          description: "Give the gift of Maison Core — from one Reformer class (€35) to a private Pilates experience. Gift cards from €35 to €250, available online.",
          canonical: "https://maisoncore.nl/gift-cards",
        }
  );

  return (
    <>
      {/* HERO */}
      <section className="bg-petal" data-testid="gifts-hero">
        <div className="mx-auto grid max-w-[1600px] items-center gap-0 md:grid-cols-2">
          <div className="px-6 pb-16 pt-32 md:px-16 md:pb-24 md:pt-44">
            <SectionLabel>{c.label}</SectionLabel>
            <Reveal>
              <h1 className="mt-8 font-serif text-5xl leading-[0.98] sm:text-6xl lg:text-7xl">{c.heroTitle}</h1>
            </Reveal>
            <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-onyx/70">{c.heroLead}</p>
            <button
              onClick={scrollToCards}
              className="label mt-12 inline-block border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx"
              data-testid="gifts-hero-cta"
            >
              {c.heroCta} ↓
            </button>
          </div>
          <ImageReveal src={IMAGES.detail} alt="Maison Core studio detail" className="aspect-[4/5] md:aspect-auto md:h-full md:min-h-[560px]" />
        </div>
      </section>

      {/* GIFT CARDS GRID */}
      <section id="gift-cards-grid" className="scroll-mt-24 bg-petal" data-testid="gifts-grid">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {c.cards.map((card, i) => (
              <GiftCard key={card.name} card={card} c={c} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* A MORE PERSONAL GIFT */}
      <section className="bg-blush/40" data-testid="gifts-personal">
        <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28">
          <ImageReveal src={IMAGES.equipCarriage} alt="Private Reformer Pilates at Maison Core" className="aspect-[4/3]" />
          <div>
            <SectionLabel>{c.personalLabel}</SectionLabel>
            <Reveal>
              <h2 className="mt-8 font-serif text-5xl leading-[0.98] md:text-6xl">{c.personal.name}</h2>
            </Reveal>
            <p className="mt-4 font-serif text-4xl text-terracotta">{c.personal.price}</p>
            <p className="mt-6 max-w-md font-sans text-lg leading-relaxed text-onyx/70">{c.personal.body}</p>
            <a
              href={c.personal.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("gift_card_click", { gift: "Private 1:1" })}
              className="label mt-10 inline-block border border-onyx px-10 py-5 transition-colors duration-500 hover:bg-onyx hover:text-petal"
              data-testid="gifts-personal-cta"
            >
              {c.personal.cta}
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-petal" data-testid="gifts-how">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.howLabel}</SectionLabel>
          <div className="mt-14 grid gap-12 md:grid-cols-3 md:gap-10">
            {c.steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="border-t border-onyx/20 pt-8" data-testid={`gifts-step-${i}`}>
                  <p className="font-serif text-4xl text-terracotta">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-6 font-serif text-3xl leading-snug">{s.t}</h3>
                  <p className="mt-4 max-w-sm font-sans text-base leading-relaxed text-onyx/65">{s.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-onyx text-petal" data-testid="gifts-closing">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-10 px-6 py-24 md:flex-row md:items-end md:justify-between md:px-12 md:py-32">
          <div className="max-w-2xl">
            <Reveal>
              <h2 className="font-serif text-5xl leading-[0.98] md:text-7xl">{c.closingTitle}</h2>
            </Reveal>
            <p className="mt-8 font-sans text-lg leading-relaxed text-petal/75">{c.closingBody}</p>
          </div>
          <button
            onClick={scrollToCards}
            className="label shrink-0 border border-petal bg-petal px-10 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal"
            data-testid="gifts-closing-cta"
          >
            {c.closingCta}
          </button>
        </div>
      </section>
    </>
  );
}
